import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Slot } from "radix-ui"

import { Spinner } from "@/components/ui/spinner"

const buttonVariants = cva(
  "relative inline-flex shrink-0 items-center justify-center gap-1.5 rounded-sm border border-transparent text-button whitespace-nowrap focus-ring transition-[color,background-color,border-color,border-radius] duration-brand ease-brand hover:rounded-hover disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:not-data-loading:opacity-50 aria-invalid:border-destructive aria-invalid:focus-visible:outline-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Black box, white text; yellow on black. Every filled button turns
        // Yellow Light on hover and rounds its corners, as on yllw.com.
        default:
          "bg-primary text-primary-foreground hover:bg-brand-yellow-hover hover:text-brand-yellow-foreground active:bg-brand-yellow active:text-brand-yellow-foreground",
        // School Bus Yellow with black text: the call to action. One per
        // view. Turns white on the yellow surface.
        yellow:
          "bg-brand-yellow text-brand-yellow-foreground hover:bg-brand-yellow-hover active:bg-brand-yellow",
        // Foggy Gray with black text: the site's default button, for
        // actions that sit beside a primary one on a marketing page.
        fog: "bg-brand-fog text-brand-fog-foreground hover:bg-brand-yellow-hover hover:text-brand-yellow-foreground active:bg-brand-yellow",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary-hover active:bg-secondary-hover",
        // The site's secondary button: a 1px outline in the general UI
        // border colour, that fills yellow on hover. Not the stronger
        // --input contrast token inputs/selects need — a button's own text
        // already identifies it, so the lighter, calmer border is enough.
        outline:
          "border-border bg-transparent text-foreground hover:border-brand-yellow hover:bg-brand-yellow hover:text-brand-yellow-foreground active:bg-brand-yellow-hover",
        ghost:
          "text-foreground hover:bg-secondary-hover active:bg-secondary-hover",
        // Only on photos: the site's alternate button, white with black
        // text, yellow on hover.
        image:
          "bg-white text-black hover:bg-yllw-yellow-light active:bg-yllw-yellow",
        destructive:
          "bg-destructive text-destructive-foreground hover:rounded-sm hover:bg-destructive-hover active:bg-destructive-pressed",
        // min-h keeps the 24px minimum hit target (WCAG 2.5.8) when the height
        // is released for inline use.
        link: "h-auto! min-h-control-xs px-0! text-ui font-medium text-foreground underline underline-offset-4 hover:rounded-sm hover:decoration-2",
      },
      size: {
        md: "h-control-md px-4 has-[svg:not([data-slot=spinner])]:px-3.5",
        /** @deprecated Use `md`. */
        default: "h-control-md px-4 has-[svg:not([data-slot=spinner])]:px-3.5",
        xs: "h-control-xs gap-1 rounded-sm px-2 text-meta has-[svg:not([data-slot=spinner])]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-control-sm gap-1 px-3 has-[svg:not([data-slot=spinner])]:px-2.5",
        lg: "h-control-lg gap-2 px-6 has-[svg:not([data-slot=spinner])]:px-5",
        icon: "size-control-md",
        "icon-xs":
          "size-control-xs rounded-sm [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-control-sm",
        "icon-md": "size-control-md",
        "icon-lg": "size-control-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
)

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    loading?: boolean
  }

function Button({
  className,
  variant = "default",
  size = "md",
  asChild = false,
  loading = false,
  disabled,
  children,
  onClick,
  onKeyDown,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button"
  const inert = loading || (asChild && !!disabled)

  // Loading keeps the element enabled so it holds keyboard focus (a disabled
  // button drops focus to <body>). aria-disabled announces it, and the
  // handlers below stop a second submit or navigation.
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-loading={loading || undefined}
      aria-busy={loading || undefined}
      aria-disabled={inert || undefined}
      disabled={asChild ? undefined : disabled}
      className={cn(buttonVariants({ variant, size, className }))}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        if (inert) {
          event.preventDefault()
          return
        }
        onClick?.(event)
      }}
      onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (inert && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault()
          return
        }
        onKeyDown?.(event)
      }}
      {...props}
    >
      {loading && asChild ? (
        // Slot looks for Slottable among its direct children, so no fragment.
        [
          <Spinner
            key="spinner"
            role={undefined}
            aria-label={undefined}
            aria-hidden
          />,
          <Slot.Slottable key="content">{children}</Slot.Slottable>,
        ]
      ) : loading ? (
        <>
          <span
            data-slot="button-content"
            className="inline-flex items-center gap-[inherit] opacity-0"
          >
            {children}
          </span>
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner role={undefined} aria-label={undefined} aria-hidden />
          </span>
        </>
      ) : (
        children
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
export type { ButtonProps }
